import { Box, Flex, Text } from "@chakra-ui/react";
import React from "react";
import {
  formatMistakeKind,
  getQuestionTone,
  mistakeCorrectionText,
  writingReportFields,
} from "./exam-feedback-pdf";
import MathRichText from "./math-rich-text";

const TONE_STYLES = {
  correct: {
    bar: "#D4F0E1",
    accent: "#1F9D73",
    score: "#148F77",
  },
  partial: {
    bar: "#FDE6C4",
    accent: "#E67E22",
    score: "#D35400",
  },
  incorrect: {
    bar: "#F8D7D7",
    accent: "#C0392B",
    score: "#C0392B",
  },
};

function ReportBlock({ title, children, bg, border }) {
  return (
    <Box bg={bg} borderLeft="4px solid" borderLeftColor={border} px={3} py={2} mb={2}>
      <Text fontSize="10px" fontWeight="800" letterSpacing="0.08em" color={border} mb={1}>
        {title}
      </Text>
      {children}
    </Box>
  );
}

function WritingReport({ question }) {
  const fields = writingReportFields(question);
  if (!Object.keys(fields).length) return null;

  return (
    <Box px={1} pt={2}>
      {fields.components ? (
        <ReportBlock title="MARK SPLIT" bg="#E8F1F6" border="#2C3E50">
          <Text fontSize="sm" color="#1B2A34">
            {fields.components
              .map((item) => `${item.name} ${item.marks_awarded}/${item.max_marks}`)
              .join("  ·  ")}
          </Text>
        </ReportBlock>
      ) : null}

      {fields.word_count != null ? (
        <Text fontSize="sm" fontWeight="800" color="#1B2A34" mb={2}>
          Word count: {fields.word_count}
        </Text>
      ) : null}

      {(fields.annotations || []).map((annotation, index) => {
        const corrected = mistakeCorrectionText(annotation);
        const kind = formatMistakeKind(annotation.kind);
        return (
          <ReportBlock
            key={`${annotation.quote || "mistake"}-${index}`}
            title="MISTAKE"
            bg="#FDF6EC"
            border="#9E6120"
          >
            {annotation.quote ? (
              <Text fontSize="sm" color="#1B2A34" whiteSpace="pre-wrap">
                {annotation.quote}
              </Text>
            ) : null}
            {kind ? (
              <Text fontSize="xs" color="#9E6120" fontWeight="800" mt={2}>
                TYPE OF MISTAKE
              </Text>
            ) : null}
            {kind ? (
              <Text fontSize="sm" color="#1B2A34">
                {kind}
              </Text>
            ) : null}
            {corrected ? (
              <Text fontSize="xs" color="#9E6120" fontWeight="800" mt={2}>
                CORRECTED SENTENCE
              </Text>
            ) : null}
            {corrected ? (
              <Text fontSize="sm" color="#1B2A34" whiteSpace="pre-wrap">
                {corrected}
              </Text>
            ) : null}
          </ReportBlock>
        );
      })}

      {fields.areas_of_improvement ? (
        <ReportBlock title="AREAS FOR IMPROVEMENT" bg="#F0F2F4" border="#4A5E73">
          {fields.areas_of_improvement.map((area, index) => (
            <Text key={`${index}-${area}`} fontSize="sm" color="#1B2A34" lineHeight="1.6">
              • {area}
            </Text>
          ))}
        </ReportBlock>
      ) : null}

      {fields.content_feedback ? (
        <ReportBlock title="FEEDBACK ON CONTENT" bg="#E8F1F6" border="#2C3E50">
          <Text fontSize="sm" color="#1B2A34" whiteSpace="pre-wrap">
            {fields.content_feedback}
          </Text>
        </ReportBlock>
      ) : null}

      {fields.grade_reason ? (
        <ReportBlock title="GRADE REASON" bg="#FDE6C4" border="#E67E22">
          <Text fontSize="sm" color="#1B2A34" whiteSpace="pre-wrap">
            {fields.grade_reason}
          </Text>
        </ReportBlock>
      ) : null}

      {fields.revised_answer ? (
        <ReportBlock title="REVISED VERSION" bg="#E8F6EF" border="#1A7359">
          <Text fontSize="sm" color="#1B2A34" whiteSpace="pre-wrap">
            {fields.revised_answer}
          </Text>
        </ReportBlock>
      ) : null}
    </Box>
  );
}

export function FeedbackQuestionCard({ question }) {
  const tone = getQuestionTone(question);
  const colors = TONE_STYLES[tone];
  const awarded = Number.isFinite(Number(question.marks_awarded))
    ? question.marks_awarded
    : "-";
  const max = Number.isFinite(Number(question.max_marks)) ? question.max_marks : "-";

  return (
    <Box mb={3}>
      <Flex
        align="center"
        bg={colors.bar}
        borderLeft="5px solid"
        borderLeftColor={colors.accent}
        px={3}
        py={1.5}
        gap={2}
      >
        <Text fontWeight="800" fontSize="sm" color="#1B2A34" flexShrink={0}>
          {question.id || "Question"}
        </Text>
        <Text fontSize="xs" fontStyle="italic" color="#7B8790" flex="1">
          Written Answer
        </Text>
        <Text fontWeight="800" fontSize="sm" color={colors.score}>
          {awarded}/{max}
        </Text>
      </Flex>

      {tone === "correct" ? (
        <Box px={3} pt={2}>
          <Text fontSize="sm" fontStyle="italic" color={colors.score}>
            Full marks awarded
          </Text>
          {question.feedback ? (
            <MathRichText mt={1} fontSize="xs" color="#6B7280">
              {question.feedback}
            </MathRichText>
          ) : null}
        </Box>
      ) : (
        <Box px={1} pt={2}>
          {question.correct_answer ? (
            <Box
              bg="#E8F1F6"
              borderLeft="4px solid"
              borderLeftColor="#2C3E50"
              px={3}
              py={2}
              mb={2}
            >
              <Text fontSize="10px" fontWeight="800" letterSpacing="0.08em" color="#2C3E50" mb={1}>
                CORRECT ANSWER
              </Text>
              <MathRichText fontSize="sm" color="#1B2A34">
                {question.correct_answer}
              </MathRichText>
            </Box>
          ) : null}
          <Box bg="#FDECEC" borderLeft="4px solid" borderLeftColor="#C0392B" px={3} py={3}>
            <Text fontSize="10px" fontWeight="800" letterSpacing="0.08em" color="#C0392B" mb={2}>
              {tone === "incorrect" ? "FEEDBACK" : "WHY MARKS WERE LOST"}
            </Text>
            {question.feedback ? (
              <MathRichText fontSize="sm" color="#1B2A34">
                {question.feedback}
              </MathRichText>
            ) : (
              <Text fontSize="sm" color="#1B2A34" lineHeight="1.8">
                Marks were lost on this question.
              </Text>
            )}
          </Box>
        </Box>
      )}
      <WritingReport question={question} />
    </Box>
  );
}

const ExamPageFeedback = ({ pageNumber, questions, contextLabel, width }) => {
  if (!questions?.length) return null;

  return (
    <Box
      mx="auto"
      mt={2}
      border="1px solid"
      borderColor="var(--border-subtle)"
      borderRadius="md"
      overflow="hidden"
      bg="#F7F9FB"
      style={{ width: width || "100%", maxWidth: "100%" }}
    >
      <Flex
        bg="#0E3D59"
        color="white"
        px={4}
        py={3}
        justify="space-between"
        align="flex-start"
        gap={3}
      >
        <Box>
          <Text fontWeight="800" fontSize="md" lineHeight="1.2">
            Feedback - Page {pageNumber}
          </Text>
          <Text fontSize="xs" opacity={0.85} mt={1}>
            {questions.map((q) => q.id).filter(Boolean).join(" · ")}
          </Text>
        </Box>
        {contextLabel ? (
          <Text fontSize="xs" opacity={0.85} textAlign="right" maxW="45%">
            {contextLabel}
          </Text>
        ) : null}
      </Flex>
      <Box px={3} py={3}>
        {questions.map((question, index) => (
          <FeedbackQuestionCard
            key={`${question.id || "q"}-${index}-${question.marks_awarded}-${question.feedback || ""}`}
            question={question}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ExamPageFeedback;
